import { useMemo } from 'react'
import { useLocation } from 'react-router-dom'

import { ModalContext } from '@domain-group/suit-theme'

import PageContext from '@contexts/pageContext'

import handleCallbackAction from '@functions/handleCallbackAction'
import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'
import getQueryParamsFromHash from '@functions/utils/getQueryParamsFromHash'
import parsePermittedQueryParams from '@functions/utils/parsePermittedQueryParams'

import useFilters from '@hooks/useFilters'
import type { UseFiltersPayload } from '@hooks/useFilters'
import useModals from '@hooks/useModals'
import useSetState from '@hooks/useSetState'

import ImportClientModal from '@modals/ImportClientModal'

import PageContent from './components/PageContent'

export type PageContextPayload = UseFiltersPayload & {
  callbacks: {
    showImportClientModal: () => void,
  },
}

const defaultState = {
  showImportClientModal: false,
}

type SetState = (state: Partial<typeof defaultState>) => void

/* eslint-disable no-undef */
const callbacks = (component: keyof typeof componentCallbacks, setState: SetState) => {
  const componentCallbacks = {
    ImportClientModal: {
      closeModal: () => setState({ showImportClientModal: false }),
      importClient: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
  }

  return componentCallbacks[component] || {}
}

const List = () => {
  const [state, setState] = useSetState(defaultState)
  const { showImportClientModal } = state

  useLocation()

  const { isArchived }: { isArchived?: string } = parsePermittedQueryParams(getQueryParamsFromHash(), ['isArchived'])
  const initDefaultFilters = isArchived ? { isArchived } : {}

  const filtersPayload = useFilters({
    defaultFilters: initDefaultFilters,
    persistFilters: true,
  })

  const {
    callbacks: filterCallbacks,
    defaultFilters,
    defaultFiltersModifiedAt,
    pageFilters,
  } = filtersPayload

  const modalContext = useModals()
  const { callbacks: { setModalData } } = modalContext

  const pageContext = useMemo(() => ({
    callbacks: {
      showImportClientModal: () => {
        setState({ showImportClientModal: true })
      },
      ...filterCallbacks,
    },
    defaultFilters,
  }), [defaultFiltersModifiedAt]) // modalContext dependency required if layering modals

  return (
    <PageContext.Provider value={pageContext}>
      <ModalContext.Provider value={modalContext}>
        <PageContent pageFilters={pageFilters} />

        <ImportClientModal
          callbacks={callbacks('ImportClientModal', setState)}
          showModal={showImportClientModal}
        />
      </ModalContext.Provider>
    </PageContext.Provider>
  )
}

export default List
